import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { approvalsApi, TimesheetWithUser, ExpenseWithUser } from '../services/approvals';
import { clientsApi, Client } from '../services/clients';
import { expensesApi } from '../services/expenses';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Link from '@mui/material/Link';

const Approvals: React.FC = () => {
  const [timesheets, setTimesheets] = useState<TimesheetWithUser[]>([]);
  const [expenses, setExpenses] = useState<ExpenseWithUser[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedTimesheet, setSelectedTimesheet] = useState<TimesheetWithUser | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<ExpenseWithUser | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingExpense, setRejectingExpense] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [approvalsData, clientsData] = await Promise.all([
        approvalsApi.getAll(),
        clientsApi.getAll(),
      ]);
      setTimesheets(approvalsData.timesheets || []);
      setExpenses(approvalsData.expenses || []);
      setClients(clientsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const getClientById = (clientId: string): Client | undefined => {
    return clients.find(c => c.id === clientId);
  };

  const getTotalHours = (entries: any[]): number => {
    return entries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
  };

  const handleApproveTimesheet = async (timesheet: TimesheetWithUser) => {
    try {
      setError(null);
      await approvalsApi.approveTimesheet(timesheet.id);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve timesheet');
    }
  };

  const handleRejectTimesheet = async () => {
    if (!selectedTimesheet) return;
    
    try {
      setError(null);
      await approvalsApi.rejectTimesheet(selectedTimesheet.id, rejectReason);
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedTimesheet(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject timesheet');
    }
  };

  const handleOpenRejectDialog = (timesheet: TimesheetWithUser) => {
    setSelectedTimesheet(timesheet);
    setSelectedExpense(null);
    setRejectingExpense(false);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleViewTimesheetDetails = (timesheet: TimesheetWithUser) => {
    setSelectedTimesheet(timesheet);
    setSelectedExpense(null);
    setViewDialogOpen(true);
  };

  const handleApproveExpense = async (expense: ExpenseWithUser) => {
    try {
      setError(null);
      await approvalsApi.approveExpense(expense.id);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve expense');
    }
  };

  const handleRejectExpense = async () => {
    if (!selectedExpense) return;
    
    try {
      setError(null);
      await approvalsApi.rejectExpense(selectedExpense.id, rejectReason);
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedExpense(null);
      setRejectingExpense(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject expense');
    }
  };

  const handleOpenRejectExpenseDialog = (expense: ExpenseWithUser) => {
    setSelectedExpense(expense);
    setSelectedTimesheet(null);
    setRejectingExpense(true);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleViewExpenseDetails = (expense: ExpenseWithUser) => {
    setSelectedExpense(expense);
    setSelectedTimesheet(null);
    setViewDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Approvals
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={`Timesheets (${timesheets.length})`} />
          <Tab label={`Expenses (${expenses.length})`} />
        </Tabs>

        {/* Timesheets Tab */}
        {tabValue === 0 && (
          <>
            {timesheets.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No timesheets pending approval.
                </Typography>
              </Box>
            ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Entries</TableCell>
                <TableCell>Total Hours</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timesheets.map((timesheet) => {
                const totalHours = getTotalHours(timesheet.entries);
                const uniqueClients = Array.from(new Set(timesheet.entries.map(e => e.clientId)));
                const clientNames = uniqueClients.map(id => getClientById(id)?.name || 'Unknown').join(', ');

                return (
                  <TableRow key={timesheet.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {timesheet.userName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {timesheet.userEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {format(parseISO(timesheet.startDate), 'MMM dd')} - {format(parseISO(timesheet.endDate), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>{timesheet.entries.length}</TableCell>
                    <TableCell>{totalHours.toFixed(1)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {clientNames || 'No clients'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewTimesheetDetails(timesheet)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Approve">
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => handleApproveTimesheet(timesheet)}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleOpenRejectDialog(timesheet)}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
            )}
          </>
        )}

        {/* Expenses Tab */}
        {tabValue === 1 && (
          <>
            {expenses.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No expenses pending approval.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Receipt</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenses.map((expense) => (
                      <TableRow key={expense.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {expense.userName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {expense.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>{expense.description}</TableCell>
                        <TableCell>
                          <Chip label={expense.category} size="small" />
                        </TableCell>
                        <TableCell>{expense.vendor || '-'}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(expense.amount, expense.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {expense.receiptPath ? (
                            <Link
                              href={expensesApi.getReceiptUrl(expense.receiptPath)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton size="small">
                                <AttachFileIcon />
                              </IconButton>
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewExpenseDetails(expense)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleApproveExpense(expense)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleOpenRejectExpenseDialog(expense)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTimesheet ? `Timesheet Details - ${selectedTimesheet.userName}` : 
           selectedExpense ? `Expense Details - ${selectedExpense.userName}` : 'Details'}
        </DialogTitle>
        <DialogContent>
          {selectedTimesheet && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Period: {format(parseISO(selectedTimesheet.startDate), 'MMM dd, yyyy')} - {format(parseISO(selectedTimesheet.endDate), 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Entries:
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Hours</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedTimesheet.entries.map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell>{format(parseISO(entry.date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{getClientById(entry.clientId)?.name || 'Unknown'}</TableCell>
                        <TableCell>{entry.hours}</TableCell>
                        <TableCell>{entry.projectDescription}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Total Hours: {getTotalHours(selectedTimesheet.entries).toFixed(1)}
              </Typography>
            </Box>
          )}
          {selectedExpense && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Date: {format(parseISO(selectedExpense.expenseDate), 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Description: {selectedExpense.description}
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Amount: {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Category: <Chip label={selectedExpense.category} size="small" />
              </Typography>
              {selectedExpense.vendor && (
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  Vendor: {selectedExpense.vendor}
                </Typography>
              )}
              {selectedExpense.notes && (
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  Notes: {selectedExpense.notes}
                </Typography>
              )}
              {selectedExpense.receiptPath && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Receipt:</Typography>
                  <Link
                    href={expensesApi.getReceiptUrl(selectedExpense.receiptPath)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Receipt
                  </Link>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>
          {rejectingExpense ? 'Reject Expense' : 'Reject Timesheet'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject this {rejectingExpense ? 'expense' : 'timesheet'}? 
            {rejectingExpense ? ' It will be marked as rejected.' : ' It will be set back to draft status.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={rejectingExpense ? handleRejectExpense : handleRejectTimesheet} 
            color="error" 
            variant="contained"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Approvals;





